import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { RevealLines } from "./RevealLines";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/04930588-728d-4dc0-a3d2-156f4b682011/images/8e8ffad1fed2719f9b377119df77a86bc3035f988b284c6a4dc6d29d00fd2488.jpeg";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section
      ref={ref}
      data-testid="omni-hero"
      className="relative min-h-[100svh] w-full overflow-hidden pt-28 sm:pt-32"
    >
      <div className="absolute inset-0 omni-spotlight" />

      {/* Floating product image — right, clipped, parallax */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="pointer-events-none absolute right-0 top-16 z-0 h-[62vh] w-[70vw] sm:w-[52vw] md:w-[44vw] lg:w-[38vw] overflow-hidden"
      >
        <img
          src={HERO_IMG}
          alt="omni-score digital umpire tracking a table tennis rally"
          className="h-full w-full object-cover object-center opacity-90 [mask-image:linear-gradient(to_left,black_55%,transparent)]"
        />
      </motion.div>

      <motion.div
        style={{ y: textY }}
        className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 lg:px-12"
      >
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.6 }}
          className="mb-8 inline-flex items-center gap-2.5"
          data-testid="hero-eyebrow"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-[#f5a623]" />
          <span className="text-[0.7rem] font-bold uppercase tracking-[0.24em] text-[#f5a623]">
            AI-Powered Digital Umpire
          </span>
        </motion.div>

        <h1 className="font-display font-bold tracking-tight text-[#fafafa] text-[3rem] leading-[0.98] sm:text-[4.5rem] md:text-[5.75rem] lg:text-[6.75rem] max-w-[16ch]">
          <RevealLines
            lines={["An AI umpire", "for table tennis", "that never blinks."]}
            delay={0.35}
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.7 }}
          className="mt-8 max-w-xl text-lg leading-relaxed text-[#c9c9cf]"
          data-testid="hero-subtitle"
        >
          Omni automates the score, applies official ITTF rules and makes every
          match smarter. You just play — Omni takes care of the rest.
          <span className="mt-2 block text-base italic text-[#8a8a92]">
            Omni automatizza il punteggio, applica le regole ITTF ufficiali e
            rende ogni partita più intelligente. Tu giochi, al resto pensa Omni.
          </span>
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          className="mt-16 flex items-center gap-3 text-[#8a8a92]"
        >
          <ArrowDown size={16} className="animate-bounce" />
          <span className="text-xs uppercase tracking-[0.2em]">
            Scroll / Scorri
          </span>
        </motion.div>
      </motion.div>
    </section>
  );
};
